import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Box, CircularProgress, Grid } from '@mui/material';

import Step1Active from 'src/assets/svg/followup/Step_01_active.svg';
import Step1Inactive from 'src/assets/svg/followup/Step_01_inactive.svg';
import Step2Active from 'src/assets/svg/followup/Step_02_active.svg';
import Step2Inactive from 'src/assets/svg/followup/Step_02_inactive.svg';
import Step3Active from 'src/assets/svg/followup/Step_03_active.svg';
import Step3Inactive from 'src/assets/svg/followup/Step_03_inactive.svg';
import Step4Active from 'src/assets/svg/followup/Step_04_active.svg';
import Step4Inactive from 'src/assets/svg/followup/Step_04_inactive.svg';
import { PlanActionStepStatus } from 'src/constants/actionPlan.ts';
import { SECTION } from 'src/constants/section.ts';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import RevertActionPhaseModal from 'src/components/common/RevertActionPhaseModal';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';

import Step from './Step';

import { styles } from './index.styles';

export const ACTION_PLAN_OVERLAY_TEST_ID = 'action-plan-overlay-test-id';
export const NO_ACTION_PLAN_EXISTS_TEST_ID = 'no-action-plan-exists';

const steps = [
  {
    active: Step1Active,
    inactive: Step1Inactive,
    label: 'Understand',
  },
  {
    active: Step2Active,
    inactive: Step2Inactive,
    label: 'Dialog',
  },
  {
    active: Step3Active,
    inactive: Step3Inactive,
    label: 'Action',
  },
  {
    active: Step4Active,
    inactive: Step4Inactive,
    label: 'Review',
  },
];

const FollowUp = ({ currentOrgUnit }) => {
  const history = useHistory();
  const { user } = useUserContext();
  const { getPermissionForModuleType } = useUserPermissions();
  const { t } = useTranslation('planAction');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [editable, setEditable] = useState();
  const {
    actionPlans,
    increaseOrgUnitStep,
    planActionStatus,
    resetPlanActionContext,
  } = usePlanActionContext();

  // ? This looks super awkward.
  // ? Error responses should not be treated as valid answer to determine user permissions.
  async function checkForWritePermission() {
    const orgUnitWithWritePermissionIds = getPermissionForModuleType(
      SECTION.PLAN_ACTION,
    )
      ?.org_units.filter((org_unit) => !org_unit.read_only)
      ?.map((org_unit) => org_unit.id);

    setEditable(
      await HttpService.get(
        `${apiPaths.ORG_UNIT}${currentOrgUnit.id}/is_descendant_of/`,
        { ancestor_candidates: orgUnitWithWritePermissionIds },
      )
        .then(() => true)
        .catch(() => false),
    );
  }

  const revertActionPhase = async () => {
    await increaseOrgUnitStep(PlanActionStepStatus.DIALOG);
    resetPlanActionContext();
    setShowRevertModal(false);
  };

  useEffect(() => {
    if (currentOrgUnit) {
      checkForWritePermission();
    }
  }, [currentOrgUnit]);

  const handleOnClick = useCallback(
    (index) => {
      if (index === PlanActionStepStatus.DIALOG) {
        if (planActionStatus.value) {
          if (planActionStatus.value.step < PlanActionStepStatus.ACTION) {
            history.push(routePaths.PLAN_ACTION_CREATE);
          } else {
            setShowRevertModal(true);
          }
        }
      } else {
        increaseOrgUnitStep(index);
      }
    },
    [planActionStatus, history, increaseOrgUnitStep],
  );

  if (
    !user ||
    !planActionStatus ||
    (!planActionStatus.value &&
      !(planActionStatus.loading || planActionStatus.error))
  )
    return <Box data-test-id={NO_ACTION_PLAN_EXISTS_TEST_ID} />;

  return (
    <Grid container direction="row" sx={styles.content}>
      {steps.map((description, index) => (
        <Step
          {...description}
          actionButtonOnClick={
            index === PlanActionStepStatus.DIALOG
              ? () => history.push(routePaths.PLAN_ACTION_CREATE)
              : null
          }
          actionButtonLabel={
            index === PlanActionStepStatus.DIALOG
              ? t('OrgUnitStatus.ActionButtons.Action')
              : null
          }
          actionPlanCount={actionPlans ? actionPlans.length : 0}
          description={t(`OrgUnitStatus.Descriptions.${description.label}`)}
          disabled={!editable}
          key={description.label}
          label={t(`OrgUnitStatus.${description.label}`)}
          onClick={() => handleOnClick(index)}
          state={planActionStatus.value ? planActionStatus.value.step : -1}
          step={index}
        />
      ))}

      {(planActionStatus.loading || planActionStatus.error) && (
        <Box sx={styles.overlay} data-test-id={ACTION_PLAN_OVERLAY_TEST_ID}>
          {planActionStatus.loading && <CircularProgress />}
          {planActionStatus.error && (
            <ErrorMessage>{planActionStatus.error}</ErrorMessage>
          )}
        </Box>
      )}

      <RevertActionPhaseModal
        handleDelete={revertActionPhase || (() => undefined)}
        onClose={() => setShowRevertModal(false)}
        showModal={showRevertModal}
      />
    </Grid>
  );
};

export default FollowUp;
